/**
 * Push Notifications Service
 * Handles push notification registration, listening, and token management
 */

import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';

const API_URL = process.env.REACT_APP_BACKEND_URL;

class PushNotificationService {
  listeners = [];
  token = null;
  
  /**
   * Initialize push notifications
   * Call this early in your app lifecycle
   */
  async initialize() {
    // Only run on native platforms
    if (!Capacitor.isNativePlatform()) {
      console.log('Push notifications not available on web');
      return false;
    }
    
    try {
      // Check current permission status
      let permStatus = await PushNotifications.checkPermissions();
      
      if (permStatus.receive === 'prompt') {
        // Request permission
        permStatus = await PushNotifications.requestPermissions();
      }
      
      if (permStatus.receive !== 'granted') {
        console.warn('Push notification permission denied');
        return false;
      }
      
      // Register with FCM/APNs
      await PushNotifications.register();
      
      // Set up listeners
      this.setupListeners();
      
      return true;
    } catch (error) {
      console.error('Push notification initialization failed:', error);
      return false;
    }
  }
  
  /**
   * Set up event listeners for push notifications
   */
  setupListeners() {
    // Registration success
    const registrationListener = PushNotifications.addListener(
      'registration',
      (token) => {
        console.log('Push registration token:', token.value);
        this.token = token.value;
        this.sendTokenToBackend(token.value);
      }
    );
    this.listeners.push(registrationListener);
    
    // Registration error
    const errorListener = PushNotifications.addListener(
      'registrationError',
      (error) => {
        console.error('Push registration error:', error);
      }
    );
    this.listeners.push(errorListener);
    
    // Notification received while app is open
    const receivedListener = PushNotifications.addListener(
      'pushNotificationReceived',
      (notification) => {
        console.log('Push notification received:', notification);
        this.handleNotification(notification);
      }
    );
    this.listeners.push(receivedListener);
    
    // User tapped on notification
    const actionListener = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action) => {
        console.log('Push notification action:', action);
        this.handleNotificationAction(action);
      }
    );
    this.listeners.push(actionListener);
  }
  
  /**
   * Send device token to backend for storage
   */
  async sendTokenToBackend(token) {
    try {
      const response = await fetch(`${API_URL}/api/device-tokens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          token,
          platform: Capacitor.getPlatform()
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to register device token');
      }
      
      console.log('Device token registered successfully');
    } catch (error) {
      console.error('Failed to send token to backend:', error);
    }
  }
  
  /**
   * Handle incoming notification
   */
  handleNotification(notification) {
    // You can customize this to show in-app notifications
    // or update app state based on notification data
    const { title, body, data } = notification;
    
    // Example: Show a toast notification
    if (window.showNotificationToast) {
      window.showNotificationToast(title, body);
    }
  }
  
  /**
   * Handle notification action (user tapped on notification)
   */
  handleNotificationAction(action) {
    const { notification } = action;
    const data = notification.data || {};
    
    // Navigate based on notification type
    if (data.type === 'new_matches') {
      window.location.href = `/my-matches/${data.sessionId}`;
    } else if (data.type === 'new_lead') {
      window.location.href = '/dealer/dashboard?tab=pipeline';
    } else if (data.type === 'email_opened') {
      window.location.href = '/dealer/dashboard?tab=email-center';
    }
  }
  
  /**
   * Get the current device token
   */
  getToken() {
    return this.token;
  }
  
  /**
   * Clean up listeners
   */
  cleanup() {
    this.listeners.forEach(listener => listener.remove());
    this.listeners = [];
  }
}

export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
